/**
 * features/admin/FamiliesManager.tsx
 * ------------------------------------------------------------------
 * Admin screen for the school-wide Family grouping. Create a family,
 * set its single primary contact (the comms/billing de-dup target),
 * and add/remove member students. The family ↔ student link is kept
 * consistent by lib/families.ts. Gated by canManageFamilies.
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  Search,
  UserPlus,
  Mail,
  Phone,
  Layers,
  UserMinus,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  GradientHero,
  SoftCard,
  StatChip,
  AccentButton,
  glassFieldClass,
} from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import {
  saveFamily,
  deleteFamily,
  familyMembers,
  type FamilyInput,
} from '@/lib/families';
import type { FamilyBillingPlan } from '@/types';
import type { Family, Student } from '@/types';

interface FormState {
  id?: string;
  name: string;
  contactName: string;
  contactEmail: string;
  contactWhatsapp: string;
  consentToSend: boolean;
  billingAddress: string;
  billingPlanKind: 'standard' | 'custom' | 'prepayYear';
  billingSplits: string;
  memberIds: string[];
  guardianRef?: { studentId: string; guardianId: string };
}

const EMPTY: FormState = {
  name: '',
  contactName: '',
  contactEmail: '',
  contactWhatsapp: '',
  consentToSend: false,
  billingAddress: '',
  billingPlanKind: 'standard',
  billingSplits: '50, 50',
  memberIds: [],
};

const primaryGuardian = (s?: Student) => {
  const gs = s?.admissions?.guardians || [];
  return gs.find((g) => g.isPrimaryContact) || gs[0] || null;
};

export default function FamiliesManager() {
  const { families, students, loading } = useData();
  const { userData } = useAuth();

  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [memberQuery, setMemberQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });

  const studentById = useMemo(
    () => new Map(students.map((s) => [s.id, s])),
    [students],
  );
  const familyIdByStudent = useMemo(
    () => new Map(students.filter((s) => s.familyId).map((s) => [s.id, s.familyId!])),
    [students],
  );

  const stats = useMemo(() => {
    const multi = families.filter((f) => f.studentIds.length > 1).length;
    const grouped = students.filter((s) => s.familyId).length;
    return { total: families.length, multi, grouped, unlinked: students.length - grouped };
  }, [families, students]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = [...families].sort((a, b) => a.name.localeCompare(b.name));
    if (!q) return list;
    return list.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        f.primaryContact?.name?.toLowerCase().includes(q) ||
        familyMembers(f, students).some((s) => s.fullName.toLowerCase().includes(q)),
    );
  }, [families, students, search]);

  const memberMatches = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return [];
    return students
      .filter(
        (s) =>
          !form.memberIds.includes(s.id) &&
          (s.fullName.toLowerCase().includes(q) ||
            (s.admissionNumber || '').toLowerCase().includes(q)),
      )
      .slice(0, 8);
  }, [students, memberQuery, form.memberIds]);

  function openNew() {
    setForm(EMPTY);
    setMemberQuery('');
    setShowForm(true);
  }

  function openEdit(f: Family) {
    const pc = f.primaryContact || { name: '' };
    setForm({
      id: f.id,
      name: f.name,
      contactName: pc.name || '',
      contactEmail: pc.email || '',
      contactWhatsapp: pc.whatsapp || '',
      consentToSend: pc.consentToSend ?? false,
      billingAddress: f.billingAddress || '',
      billingPlanKind: f.billingPlan?.kind ?? 'standard',
      billingSplits: f.billingPlan?.splits?.length ? f.billingPlan.splits.join(', ') : '50, 50',
      memberIds: [...f.studentIds],
      guardianRef: pc.guardianRef,
    });
    setMemberQuery('');
    setShowForm(true);
  }

  function addMember(s: Student) {
    setForm((f) => ({ ...f, memberIds: [...f.memberIds, s.id] }));
    setMemberQuery('');
  }
  function removeMember(id: string) {
    setForm((f) => ({ ...f, memberIds: f.memberIds.filter((x) => x !== id) }));
  }

  /** Convenience: pull the primary contact from a chosen member's guardian. */
  function useGuardianOf(studentId: string) {
    const g = primaryGuardian(studentById.get(studentId));
    if (!g) return;
    setForm((f) => ({
      ...f,
      contactName: g.fullName || f.contactName,
      contactEmail: g.email || f.contactEmail,
      contactWhatsapp: g.whatsapp || g.phone || f.contactWhatsapp,
      consentToSend: g.consentToSend ?? f.consentToSend,
      guardianRef: { studentId, guardianId: g.id },
    }));
  }

  async function submit() {
    if (!form.name.trim()) {
      setMsg({ ok: false, text: 'Family name is required.' });
      return;
    }
    if (form.memberIds.length === 0) {
      setMsg({ ok: false, text: 'Add at least one student.' });
      return;
    }
    setSaving(true);
    setSaveModal({ show: true, status: 'saving', message: form.id ? 'Updating family…' : 'Creating family…' });
    try {
      const customSplits = form.billingSplits.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0);
      const billingPlan: FamilyBillingPlan =
        form.billingPlanKind === 'custom'
          ? { kind: 'custom', splits: customSplits.length ? customSplits : [50, 50] }
          : { kind: form.billingPlanKind };
      const input: FamilyInput = {
        id: form.id,
        name: form.name,
        memberIds: form.memberIds,
        billingAddress: form.billingAddress,
        billingPlan,
        primaryContact: {
          name: form.contactName.trim() || form.name.trim(),
          email: form.contactEmail.trim() || undefined,
          whatsapp: form.contactWhatsapp.trim() || undefined,
          consentToSend: form.consentToSend,
          guardianRef: form.guardianRef,
        },
      };
      await saveFamily(input, {
        families,
        students,
        actor: { uid: userData?.uid, name: userData?.displayName },
      });
      setSaveModal({ show: true, status: 'success', message: form.id ? 'Family updated.' : 'Family created.' });
      setShowForm(false);
      setForm(EMPTY);
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function remove(f: Family) {
    try {
      await deleteFamily(f);
      setConfirmDelete(null);
      setMsg({ ok: true, text: `Deleted ${f.name}; its students are now unlinked.` });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    }
  }

  if (loading) return <Spinner label="Loading families…" />;

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Users}
        title="Families"
        subtitle={`${stats.total} families · groups siblings for combined billing and one-message comms`}
        actions={<AccentButton icon={Plus} onClick={openNew}>New Family</AccentButton>}
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip tone="blue" icon={Users} value={stats.total} label="Families" />
        <StatChip tone="purple" icon={Layers} value={stats.multi} label="With siblings" />
        <StatChip tone="green" icon={CheckCircle2} value={stats.grouped} label="Students grouped" />
        <StatChip tone={stats.unlinked ? 'gold' : 'slate'} icon={UserMinus} value={stats.unlinked} label="Unlinked" />
      </div>

      {msg && (
        <SoftCard>
          <div className={`flex items-center gap-2 text-sm ${msg.ok ? 'text-emerald-700' : 'text-red-700'}`}>
            {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} {msg.text}
          </div>
        </SoftCard>
      )}

      {/* Editor */}
      {showForm && (
        <SoftCard>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">{form.id ? 'Edit Family' : 'New Family'}</h3>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>

          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Family name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Phiri Family" className={glassFieldClass('')} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Billing / correspondence address</label>
                <input value={form.billingAddress} onChange={(e) => setForm({ ...form, billingAddress: e.target.value })} placeholder="Optional" className={glassFieldClass('')} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Payment plan</label>
                <select value={form.billingPlanKind} onChange={(e) => setForm({ ...form, billingPlanKind: e.target.value as FormState['billingPlanKind'] })} className={glassFieldClass('')}>
                  <option value="standard">Standard (school default split)</option>
                  <option value="custom">Custom split</option>
                  <option value="prepayYear">Pay full year in advance</option>
                </select>
                {form.billingPlanKind === 'custom' && (
                  <>
                    <input value={form.billingSplits} onChange={(e) => setForm({ ...form, billingSplits: e.target.value })} placeholder="e.g. 80, 20" className={glassFieldClass('mt-2')} />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Percentage per instalment, comma-separated (should sum to 100).</p>
                  </>
                )}
                {form.billingPlanKind === 'prepayYear' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Billed the whole year up front; the prepayment discount applies.</p>
                )}
              </div>
            </div>

            {/* Primary contact */}
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Primary contact <span className="font-normal text-gray-400">— the single recipient for statements, reminders & notices</span></span>
                {form.memberIds.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => { if (e.target.value) useGuardianOf(e.target.value); }}
                    className="text-xs rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 px-2 py-1"
                    title="Copy contact from a member's guardian"
                  >
                    <option value="">Use a member's guardian…</option>
                    {form.memberIds.map((id) => {
                      const s = studentById.get(id);
                      const g = primaryGuardian(s);
                      return <option key={id} value={id}>{s?.fullName}{g ? ` — ${g.fullName}` : ''}</option>;
                    })}
                  </select>
                )}
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value, guardianRef: undefined })} placeholder="Contact name" className={glassFieldClass('')} />
                <input value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} placeholder="Email" className={glassFieldClass('')} />
                <input value={form.contactWhatsapp} onChange={(e) => setForm({ ...form, contactWhatsapp: e.target.value })} placeholder="WhatsApp / phone" className={glassFieldClass('')} />
              </div>
              <p className="text-xs text-gray-400">
                Saving applies this email &amp; WhatsApp to every member child's primary guardian (parent invites &amp; portal) and delivery contact (report emails).
              </p>
              <label className="flex items-center gap-2 text-sm cursor-pointer text-gray-600 dark:text-gray-300">
                <input type="checkbox" checked={form.consentToSend} onChange={(e) => setForm({ ...form, consentToSend: e.target.checked })} className="rounded border-gray-300" />
                Consent to electronic delivery
              </label>
            </div>

            {/* Members */}
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 p-4 space-y-3">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Members ({form.memberIds.length})</span>
              <div className="flex flex-wrap gap-2">
                {form.memberIds.map((id) => {
                  const s = studentById.get(id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs">
                      {s?.fullName ?? id}
                      {s?.currentClass && <span className="text-blue-400">· {s.currentClass}</span>}
                      <button onClick={() => removeMember(id)} className="hover:text-red-500"><X size={13} /></button>
                    </span>
                  );
                })}
                {form.memberIds.length === 0 && <span className="text-xs text-gray-400">No students yet — search below to add.</span>}
              </div>
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)} placeholder="Search students by name or admission no.…" className={glassFieldClass('pl-9')} />
              </div>
              {memberMatches.length > 0 && (
                <div className="rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden">
                  {memberMatches.map((s) => {
                    const otherFam = familyIdByStudent.get(s.id);
                    const inOther = otherFam && otherFam !== form.id;
                    return (
                      <button key={s.id} onClick={() => addMember(s)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <span className="flex items-center gap-2">
                          <UserPlus size={14} className="text-gray-400" />
                          {s.fullName}
                          <span className="text-gray-400 text-xs">{s.currentClass}{s.admissionNumber ? ` · ${s.admissionNumber}` : ''}</span>
                        </span>
                        {inOther && <span className="text-[10px] text-amber-600 dark:text-amber-400">will move from current family</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <AccentButton variant="soft" onClick={() => setShowForm(false)}>Cancel</AccentButton>
              <AccentButton onClick={submit} disabled={saving}>{saving ? 'Saving…' : form.id ? 'Update Family' : 'Create Family'}</AccentButton>
            </div>
          </div>
        </SoftCard>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search families by name, contact, or child…" className={glassFieldClass('pl-9')} />
      </div>

      {/* List */}
      {visible.length === 0 ? (
        <SoftCard><p className="text-sm text-gray-500 text-center py-6">{families.length === 0 ? 'No families yet. Create one, or run the seed to generate them.' : 'No families match your search.'}</p></SoftCard>
      ) : (
        <div className="space-y-3">
          {visible.map((f) => {
            const members = familyMembers(f, students);
            return (
              <SoftCard key={f.id} pad="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h4 className="font-semibold text-sm text-gray-900 dark:text-white">{f.name}</h4>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium">
                        {members.length} {members.length === 1 ? 'child' : 'children'}
                      </span>
                      {f.isDemo && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">demo</span>}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {members.map((s) => (
                        <span key={s.id} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                          {s.fullName}{s.currentClass ? ` · ${s.currentClass}` : ''}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                      {f.primaryContact?.name && <span>{f.primaryContact.name}</span>}
                      {f.primaryContact?.email && <span className="flex items-center gap-1"><Mail size={11} /> {f.primaryContact.email}</span>}
                      {f.primaryContact?.whatsapp && <span className="flex items-center gap-1"><Phone size={11} /> {f.primaryContact.whatsapp}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openEdit(f)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit"><Pencil size={14} /></button>
                    {confirmDelete === f.id ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => remove(f)} className="px-2 py-1 text-xs bg-red-500 text-white rounded-lg">Delete</button>
                        <button onClick={() => setConfirmDelete(null)} className="px-2 py-1 text-xs text-gray-500">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmDelete(f.id)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-400" title="Delete"><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              </SoftCard>
            );
          })}
        </div>
      )}

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );
}
